import { authApi, isAuthError } from '@/api';
import { FormField, PrimaryButton } from '@/components';
import EurodimaLogo from '@/components/assets/EurodimaLogo';
import { LoggedOutCard } from '@/components/layout/LoggedOutCard';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AUTH_QUERY_KEY } from '@/lib/auth';
import { useLoginRedirect } from '@/hooks/useLoginRedirect';

function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { followRedirect } = useLoginRedirect();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await authApi.login({ email, password });
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      followRedirect();
    } catch (err) {
      if (isAuthError(err)) {
        setError(err.message);
      } else {
        setError(t('auth.loginError', 'An error occurred during login'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoggedOutCard>
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-48">
            <EurodimaLogo />
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-onsurface">
            {t('auth.login', 'Sign in')}
          </h1>
          <p className="mt-2 text-sm opacity-50">
            {t('auth.loginSubtitle', 'Enter your credentials to access your account')}
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField
            label={t('auth.email', 'Email')}
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoComplete="email"
          />

          <FormField
            label={t('auth.password', 'Password')}
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="current-password"
          />

          <div className="flex justify-end">
            <Link
              to="/reset-password"
              className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {t('auth.forgotPassword', 'Forgot password?')}
            </Link>
          </div>

          <PrimaryButton
            type="submit"
            isLoading={isLoading}
            fullWidth
          >
            {t('auth.loginButton', 'Sign in')}
          </PrimaryButton>
        </form>

        {/* Register link */}
        <div className="mt-6 text-center">
          <p className="text-sm ">
            <span className="opacity-50">
              {t('auth.noAccount', "Don't have an account?")}{' '}</span>
            <Link
              to="/register"
              className="text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {t('auth.registerLink', 'Create one')}
            </Link>
          </p>
        </div>
    </LoggedOutCard>
  );
}

export const Route = createFileRoute('/_auth/login')({
  component: LoginPage,
});
